import React, { useEffect, useMemo, useRef } from 'react';
import { getCanvasFormatById } from '../canvasFormats';
import { createLowerThirdRenderer } from '../render/lowerThirdRenderer';
import { getPlaybackFrame } from './livePlayback';
import { getRelayClockNow } from '../shared/liveProtocol';

const clearCanvas = (canvas) => {
  const ctx = canvas?.getContext?.('2d');
  if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
};

const drawProgramFrame = ({
  renderer,
  ctx,
  now,
  program,
  liveState,
  forceTransparentBackground,
}) => {
  if (!program) {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    return;
  }
  const fps = Math.max(1, Number(program.fps) || 60);
  const frame = getPlaybackFrame(liveState, now) ?? 0;
  renderer.drawFrame(ctx, frame, program.person, program.config, {
    activePresetId: program.activePresetId,
    fps,
    canvasWidth: program.canvasWidth,
    canvasHeight: program.canvasHeight,
    forceTransparentBackground,
  });
};

const PlaybackCanvas = ({
  person,
  config,
  fps = 60,
  activePresetId,
  aspectRatioId,
  className = '',
  label,
  compact = false,
  liveProgram = null,
  livePhase = 'idle',
  playbackStartedAt = null,
  playbackElapsedMs = 0,
  serverClockOffsetMs = 0,
  forceTransparentBackground = false,
  loopPreview = false,
  pauseWhenHidden = false,
}) => {
  const canvasRef = useRef(null);
  const rendererRef = useRef(null);
  if (!rendererRef.current) rendererRef.current = createLowerThirdRenderer();
  const format = useMemo(() => (
    liveProgram
      ? { width: liveProgram.canvasWidth, height: liveProgram.canvasHeight }
      : getCanvasFormatById(aspectRatioId)
  ), [aspectRatioId, liveProgram]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    canvas.width = format.width;
    canvas.height = format.height;
    let rafId = 0;
    const animate = (now) => {
      if (pauseWhenHidden && typeof document !== 'undefined' && document.hidden) {
        rafId = window.requestAnimationFrame(animate);
        return;
      }
      if (liveProgram) {
        drawProgramFrame({
          renderer: rendererRef.current,
          ctx,
          now: getRelayClockNow(serverClockOffsetMs),
          program: liveProgram,
          liveState: {
            program: liveProgram,
            phase: livePhase,
            playbackStartedAt,
            playbackElapsedMs,
          },
          forceTransparentBackground,
        });
      } else if (person && config) {
        const totalFrames = rendererRef.current.getDurationsForConfig(fps, config).totalFrames;
        const pauseFrames = fps;
        const loopFrame = loopPreview ? Math.floor((now / (1000 / fps))) % (totalFrames + pauseFrames) : 0;
        const activeFrame = loopPreview ? Math.min(loopFrame, totalFrames) : 0;
        rendererRef.current.drawFrame(ctx, activeFrame, person, config, {
          activePresetId,
          fps,
          staticStill: !loopPreview,
          canvasWidth: format.width,
          canvasHeight: format.height,
          forceTransparentBackground,
        });
      } else {
        clearCanvas(canvas);
      }
      rafId = window.requestAnimationFrame(animate);
    };
    rafId = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(rafId);
  }, [activePresetId, config, forceTransparentBackground, format.height, format.width, fps, livePhase, liveProgram, pauseWhenHidden, person, playbackElapsedMs, playbackStartedAt, loopPreview, serverClockOffsetMs]);

  return (
    <div className={`live-canvas-frame ${compact ? 'is-compact' : ''} ${className}`.trim()}>
      {label ? <div className="live-canvas-label">{label}</div> : null}
      <canvas ref={canvasRef} className="live-canvas" style={{ aspectRatio: `${format.width} / ${format.height}` }} />
    </div>
  );
};

export default PlaybackCanvas;
